import { useCallback, useEffect, useState } from "react";
import { Plus, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ArticleCard } from "@/components/news/ArticleCard";
import { Mascot } from "@/components/Mascot";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE = 12;

export default function BacaYuk() {
  const [keywords, setKeywords] = useState([]);
  const [active, setActive] = useState("");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shown, setShown] = useState(PAGE);
  const [input, setInput] = useState("");

  useEffect(() => {
    api.get("/keywords").then((r) => setKeywords(r.data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setShown(PAGE);
    try {
      const { data } = await api.get("/news", { params: active ? { keyword: active } : {} });
      setArticles(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => {
    load();
  }, [load, keywords.length]);

  const addKw = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    try {
      const { data } = await api.post("/keywords", { keyword: input });
      setKeywords(data);
      setInput("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const delKw = async (k) => {
    const { data } = await api.delete(`/keywords/${encodeURIComponent(k)}`);
    setKeywords(data);
    if (active === k) setActive("");
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-sm text-pink-500 font-medium">Berita dari topik favoritku</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-gray-800">Baca Yuk</h1>
        </div>
        <button onClick={load} className="p-3 rounded-full bg-white border border-pink-100 text-pink-500 hover:bg-pink-50" data-testid="news-refresh-btn" aria-label="Muat ulang">
          <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1" data-testid="keyword-chips">
        <button onClick={() => setActive("")} className={`chip ${active === "" ? "chip-on" : "chip-off"}`} data-testid="keyword-chip-all">Semua</button>
        {keywords.map((k) => (
          <span key={k} className={`chip capitalize !pr-1.5 ${active === k ? "chip-on" : "chip-off"}`}>
            <button onClick={() => setActive(k)} data-testid={`keyword-chip-${k.replace(/\s+/g, "-")}`}>{k}</button>
            <button onClick={() => delKw(k)} className="p-0.5 rounded-full hover:bg-black/10" data-testid={`keyword-delete-${k.replace(/\s+/g, "-")}`} aria-label={`Hapus ${k}`}><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
      </div>
      <form onSubmit={addKw} className="flex gap-2 max-w-md">
        <input className="field" placeholder="Tambah topik, mis. 'jennie' atau 'motogp'" value={input} onChange={(e) => setInput(e.target.value)} data-testid="add-keyword-input" />
        <button type="submit" className="px-4 rounded-2xl bg-pink-500 text-white hover:bg-pink-600" data-testid="add-keyword-btn" aria-label="Tambah topik"><Plus className="w-5 h-5" /></button>
      </form>
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-3xl bg-pink-100/60" />)}
        </div>
      ) : articles.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-gray-500" data-testid="news-empty">
          <Mascot pose="read" className="w-32" />
          <p className="mt-2 text-sm">Belum ada berita. Coba topik lain ya.</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="baca-yuk-feed">
            {articles.slice(0, shown).map((a, i) => <ArticleCard key={a.link} a={a} index={i} />)}
          </div>
          {shown < articles.length && (
            <div className="flex justify-center">
              <button onClick={() => setShown((s) => s + PAGE)} className="chip chip-off" data-testid="news-load-more">Muat lebih banyak</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
